// ──────────────────────────────────────────────────────────────────────
// D84 — Workspace Live Provider (real-time sync across all members).
//
// Owns a singleton WebSocket per workspace, fans server-pushed
// `invalidate` events out to subscribers by topic, and exposes a
// connection state for the LastSynced pill (green when live, amber
// when polling, grey when disconnected).
//
// Server → client protocol (see `/api/ws/workspace/{ws_id}` in server.py):
//   {type:'hello',      workspace_id, user_id}
//   {type:'invalidate', topic, entity_id?, actor_id?, path?}
//   {type:'ping'}
//
// Why a context (not a global)?
//   - Workspace can change at runtime (switch workspaces in-app).
//   - StrictMode double-mount: the provider runs once per app instance.
//   - Subscribers (useLiveData) auto-cleanup on unmount.
// ──────────────────────────────────────────────────────────────────────
import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';

const Ctx = createContext({
  // Connection state — one of 'live' | 'reconnecting' | 'polling' | 'disconnected'.
  // 'polling' is the soft-fail state after we've given up reconnecting for now;
  // useLiveData consumers keep their 15s interval running in this state.
  connectionState: 'disconnected',
  lastEventAt: null,           // Date | null — last time we received ANY message from the server
  subscribe: (_topic, _fn) => () => {},   // returns unsubscribe fn
  // Test-only hook — fire a synthetic invalidate (used by smoke tests
  // running in the same browser to drive useLiveData from JS).
  _emit: (_topic, _detail) => {},
});

export function useWorkspaceLive() { return useContext(Ctx); }

// Helper — same logic as the rest of the codebase: strip trailing
// slash, prefer process.env.REACT_APP_BACKEND_URL, fall back to current
// origin.
function _wsUrlFor(workspaceId, token) {
  const base = (process.env.REACT_APP_BACKEND_URL || window.location.origin).replace(/\/$/, '');
  // http(s) → ws(s).
  const wsBase = base.replace(/^http/, 'ws');
  return `${wsBase}/api/ws/workspace/${workspaceId}?token=${encodeURIComponent(token)}`;
}

const MAX_RECONNECT_ATTEMPTS = 6;
// Backoff schedule (ms): 0.5s, 1s, 2s, 4s, 8s, 15s (cap). After 6 failed
// attempts we declare 'polling' fallback and stop the WebSocket attempt
// loop (useLiveData consumers keep polling at 15s).
const RECONNECT_DELAYS_MS = [500, 1000, 2000, 4000, 8000, 15000];

export function WorkspaceLiveProvider({ workspaceId, token, children }) {
  const [connectionState, setConnectionState] = useState('disconnected');
  const [lastEventAt, setLastEventAt] = useState(null);

  // Subscribers — Map<topic, Set<refetchFn>>.
  const subsRef = useRef(new Map());
  const wsRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const attemptRef = useRef(0);
  // Identity of the active connection so we can cancel in-flight retries
  // after a workspace switch.
  const sessionRef = useRef(0);

  const subscribe = useCallback((topic, fn) => {
    if (!topic || typeof fn !== 'function') return () => {};
    const m = subsRef.current;
    if (!m.has(topic)) m.set(topic, new Set());
    m.get(topic).add(fn);
    return () => {
      const set = m.get(topic);
      if (set) {
        set.delete(fn);
        if (set.size === 0) m.delete(topic);
      }
    };
  }, []);

  const _emit = useCallback((topic, detail = null) => {
    const set = subsRef.current.get(topic);
    if (!set) return;
    for (const fn of set) {
      try { fn(detail); } catch { /* swallow — refetch failures are non-fatal */ }
    }
  }, []);

  // Open / reopen the socket. Closes the previous one (if any) first.
  const open = useCallback(() => {
    if (!workspaceId || !token) {
      setConnectionState('disconnected');
      return;
    }
    const mySession = ++sessionRef.current;
    try { wsRef.current?.close(); } catch { /* ignore */ }
    wsRef.current = null;
    setConnectionState(attemptRef.current === 0 ? 'reconnecting' : 'reconnecting');

    let ws;
    try {
      ws = new WebSocket(_wsUrlFor(workspaceId, token));
    } catch {
      // URL malformed — fall straight to polling.
      setConnectionState('polling');
      return;
    }
    wsRef.current = ws;

    ws.onopen = () => {
      if (mySession !== sessionRef.current) return;
      attemptRef.current = 0;
      setConnectionState('live');
      setLastEventAt(new Date());
    };
    ws.onmessage = (e) => {
      if (mySession !== sessionRef.current) return;
      setLastEventAt(new Date());
      let msg = null;
      try { msg = JSON.parse(e.data); } catch { return; }
      if (!msg || typeof msg !== 'object') return;
      if (msg.type === 'invalidate' && msg.topic) {
        _emit(msg.topic, msg);
      }
      // 'hello' and 'ping' are connection-health signals only — no fan-out.
    };
    ws.onerror = () => {
      // Browser console will already show the error; no need to spam.
    };
    ws.onclose = () => {
      if (mySession !== sessionRef.current) return;
      wsRef.current = null;
      attemptRef.current += 1;
      if (attemptRef.current >= MAX_RECONNECT_ATTEMPTS) {
        setConnectionState('polling');
        return;
      }
      const delay = RECONNECT_DELAYS_MS[Math.min(attemptRef.current - 1, RECONNECT_DELAYS_MS.length - 1)];
      setConnectionState('reconnecting');
      reconnectTimerRef.current = setTimeout(open, delay);
    };
  }, [workspaceId, token, _emit]);

  // Manual reconnect — re-enable WS after we fell back to polling. Used
  // by the LastSynced pill's click handler so users can force a retry.
  const forceReconnect = useCallback(() => {
    attemptRef.current = 0;
    open();
  }, [open]);

  useEffect(() => {
    // (Re)open whenever workspace or token changes.
    attemptRef.current = 0;
    open();
    return () => {
      sessionRef.current += 1;
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
      try { wsRef.current?.close(); } catch { /* ignore */ }
      wsRef.current = null;
      setConnectionState('disconnected');
    };
  }, [workspaceId, token, open]);

  // Expose forceReconnect on window for debug + the LastSynced pill.
  useEffect(() => {
    window.__stellarForceLiveReconnect = forceReconnect;
    return () => { try { delete window.__stellarForceLiveReconnect; } catch { /* noop */ } };
  }, [forceReconnect]);

  const value = {
    connectionState,
    lastEventAt,
    subscribe,
    _emit,
    forceReconnect,
  };
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
